(ns practitest.frontend.custom-views-settings
  (:require
    [practitest.frontend.common-entities.metadata :as md]
    [practitest.frontend.common.i18n :refer [format-translation
                                             get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.re-frame :as rf-common]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.navigation :as navigation]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.components.project-settings-tabs :as project-settings-tabs]
    [practitest.frontend.components.view-editor.events :as view-editor.events]
    [practitest.frontend.components2.dropdown :as dropdown]
    [practitest.frontend.components2.grid :as grid2]
    [practitest.frontend.components2.grid.row-menu :refer [row-menu]]
    [practitest.frontend.events :as events]
    [practitest.frontend.forms3.custom-field-form-common :as cf-form-common]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(def ^:private db-key :custom-views-settings)
(def ^:private default-per-page 100)

(defn- grid-table-id [project-id]
  (str "custom-views-index:" project-id))

(defn- entity-label [entity-type]
  (md/get-entity-name (md/entity-by-module entity-type) :singular-name))

(defn- yes-no-label [value?]
  (if value?
    (get-translation "generic.button.yes" "Yes")
    (get-translation "generic.button.no" "No")))

(defn- label->entity-type [entity-types label]
  (some #(when (= label (entity-label %)) %) entity-types))

(defn- label->boolean-param [label]
  (str (= label (yes-no-label true))))

(defn- server-gridfilter [entity-types gridfilter]
  (reduce-kv
    (fn [acc k v]
      (case k
        "name_link" (assoc acc "name" v)
        "entity_label" (assoc acc "entity_type"
                              (into []
                                    (keep (partial label->entity-type entity-types)) v))
        "is_public_label" (assoc acc "is_public" (label->boolean-param (first v)))
        "auto_filter_label" (assoc acc "auto_filter" (label->boolean-param (first v)))
        "owner_name" (assoc acc "owner_name" v)
        "created_at" (assoc acc "created_at" v)
        "updated_at" (assoc acc "updated_at" v)
        acc))
    {}
    ;; TODO: we really need to stop stringifying/parsing stuff when it's not necessary
    (js->clj (js/JSON.parse gridfilter))))

(def ^:private server-order-by
  {"name_link" "name"
   "entity_label" "entity_type"
   "is_public_label" "is_public"
   "owner_name" "owner_name"
   "created_at" "created_at"
   "updated_at" "updated_at"})

(defn- server-order [query]
  (if-let [field (some-> (:order_by query) name)]
    (if-let [mapped (get server-order-by field)]
      (assoc query :order_by mapped)
      (dissoc query :order_by :order_direction))
    query))

(defn- server-query [entity-types query]
  (let [query (server-order query)]
    (if-let [gridfilter (:gridfilter query)]
      (let [mapped (server-gridfilter entity-types gridfilter)]
        (if (seq mapped)
          (assoc query :gridfilter (js/JSON.stringify (clj->js mapped)))
          (dissoc query :gridfilter)))
      query)))

(defn- initial-query [table-id]
  (let [[sort-column direction] (grid-session-storage/load-sort-order table-id)]
    (cond-> {:per_page (or (grid-storage/load-per-page table-id) default-per-page)
             :page (or (grid-storage/load-current-page table-id) 1)}
      (some? sort-column)
      (assoc :order_by sort-column :order_direction (name direction)))))

;; ---- events ----

(re-frame/reg-event-fx
  ::load!
  (fn [{:keys [db]} _]
    (if (get-in db [:options :new-view-editor-enabled?])
      {:dispatch [::refresh]
       ::mixpanel/track ["Filters Settings" {"Action" "Page View"}]}
      ;; old non-SPA full refresh
      {::events/redirect
       (broutes/get-url "custom_views#index"
                        {:project_id (subs/get-current-project-id db)})})))

(re-frame/reg-event-fx
  ::refresh
  (fn [{:keys [db]} [_ query]]
    (let [project-id (subs/get-current-project-id db)
          query (or
                  query
                  (get-in db [db-key :query])
                  (initial-query (grid-table-id project-id)))]
      {:db (update db db-key merge {:query query :loading? true})
       ::rf-common/http {:url (broutes/get-url "custom_views#index"
                                               {:project_id project-id}
                                               (server-query (get-in db [db-key :entity-types]) query))
                         :on-success [::loaded]
                         :on-failure [::load-failed]}})))

(re-frame/reg-event-db
  ::loaded
  (fn [db [_ {:keys [data current_page total_count full can_add entity_types]}]]
    (update db db-key merge
            {:loading? false
             :views (or data [])
             :current-page (or current_page 1)
             :total-count (or total_count 0)
             :full-data? (boolean full)
             :can-add? (boolean can_add)
             :entity-types (or entity_types [])})))

(re-frame/reg-event-fx
  ::load-failed
  (fn [{:keys [db]} _]
    {:db (assoc-in db [db-key :loading?] false)
     :dispatch [::notifications/error2
                (get-translation "sections.notify.error-saving"
                                 "Something went wrong. Please try again")]}))

(re-frame/reg-event-db
  ::reset
  (fn [db _]
    (dissoc db db-key)))

(re-frame/reg-event-fx
  ::view-saved
  (fn [_ _]
    {:dispatch [::refresh]}))

(re-frame/reg-event-fx
  ::delete-view
  (fn [{:keys [db]} [_ view-id]]
    {:db (assoc-in db [db-key :loading?] true)
     ::mixpanel/track ["Filters Settings" {"Action" "Delete View"}]
     ::rf-common/http {:url (broutes/get-url "custom_views#destroy"
                                             {:project_id (subs/get-current-project-id db)
                                              :id view-id})
                       :method "DELETE"
                       :on-success [::view-deleted]
                       :on-failure [::load-failed]}}))

(re-frame/reg-event-fx
  ::view-deleted
  (fn [{:keys [db]} [_ {:keys [status blocked message usages]}]]
    (cond
      blocked
      {:db (update db db-key merge {:loading? false
                                    :blocked-delete {:message message :usages usages}})}

      (= status "OK")
      {:dispatch-n [[::notifications/success2
                     (get-translation "custom-views.delete.success" "Filter deleted")]
                    [::refresh]]}

      :else
      {:dispatch [::load-failed]})))

(re-frame/reg-event-db
  ::hide-blocked-delete
  (fn [db _]
    (update db db-key dissoc :blocked-delete)))

;; ---- subs ----

;; TODO: re-frame 1.3.0+ adds :-> shorthand

(re-frame/reg-sub ::state (fn [db _] (get db db-key)))
(re-frame/reg-sub ::loading? :<- [::state] (fn [s _] (boolean (:loading? s))))
(re-frame/reg-sub ::full-data? :<- [::state] (fn [s _] (boolean (:full-data? s))))
(re-frame/reg-sub ::can-add? :<- [::state] (fn [s _] (boolean (:can-add? s))))
(re-frame/reg-sub ::entity-types :<- [::state] (fn [s _] (get s :entity-types [])))
(re-frame/reg-sub ::current-page :<- [::state] (fn [s _] (get s :current-page 1)))
(re-frame/reg-sub ::total-count :<- [::state] (fn [s _] (get s :total-count 0)))
(re-frame/reg-sub ::blocked-delete :<- [::state] (fn [s _] (:blocked-delete s)))

(re-frame/reg-sub
  ::rows
  :<- [::state]
  (fn [{:keys [views]} _]
    (mapv (fn [{:keys [name url entity_type is_public auto_filter] :as view}]
            (assoc view
                   :name_link {:title name :url url}
                   :entity_label (entity-label entity_type)
                   :is_public_label (yes-no-label is_public)
                   :auto_filter_label (yes-no-label auto_filter)))
          views)))

;; ---- views ----

(defn- columns [full-data? entity-types]
  (let [yes-no [(yes-no-label true) (yes-no-label false)]]
    [{:field :name_link
      :header (get-translation "view-editor.name" "Filter Name")
      :type :internal-url
      :default-width 250
      :sortable? true
      :autofilter? true}
     {:field :is_public_label
      :header (get-translation "view-editor.is-public" "Public")
      :type :list
      :multiple? false
      :possible-values yes-no
      :default-width 90
      :sortable? true
      :autofilter? true}
     {:field :entity_label
      :header (get-translation "custom-fields.type" "Type")
      :type :list
      :multiple? true
      :possible-values (mapv entity-label entity-types)
      :default-width 120
      :sortable? true
      :autofilter? true}
     {:field :parent_name
      :header (get-translation "view-editor.parent" "Parent")
      :type :text
      :default-width 200
      :sortable? full-data?
      :autofilter? full-data?}
     {:field :auto_filter_label
      :header (get-translation "view-editor.filter-type.auto-filter" "Auto Filter")
      :type :list
      :multiple? false
      :possible-values yes-no
      :default-width 100
      :sortable? full-data?
      :autofilter? true}
     {:field :owner_name
      :header (get-translation "custom-views.column.owner" "Owner")
      ;; TODO: should the type be "user"? It's a bit bigger change though
      :type :text
      :default-width 180
      :sortable? true
      :autofilter? true}
     {:field :created_at
      :header (get-translation "generic.label.created" "Created")
      :type :date-time
      :default-width 160
      :sortable? true
      :autofilter? true}
     {:field :updated_at
      :header (get-translation "custom-fields.modified" "Modified")
      :type :date-time
      :default-width 160
      :sortable? true
      :autofilter? true}]))

(defn- row-menu-actions [{:keys [id name]}]
  [{:id (str "custom-view-" id "-edit")
    :label (get-translation "generic.button.edit" "Edit")
    :icon icons/mux-pencil
    :on-click (fn [_]
                (mixpanel/track "Filters Settings" {"Action" "Edit"})
                (re-frame/dispatch [::view-editor.events/open-edit id
                                    {:on-saved [::view-saved]}]))}
   {:id (str "custom-view-" id "-move")
    :label (get-translation "filters-tree.context-menu.move" "Move")
    :icon icons/mux-arrow-right3
    :on-click (fn [_]
                (mixpanel/track "Filters Settings" {"Action" "Move"})
                (re-frame/dispatch [::view-editor.events/open-move id
                                    {:on-saved [::view-saved]}]))}
   {:id (str "custom-view-" id "-delete")
    :label (get-translation "generic.button.delete" "Delete")
    :icon icons/mux-trash
    :confirmation {:title (get-translation "custom-views.delete.title" "Delete Filter?")
                   :body (format-translation
                           (get-translation "custom-views.delete.body"
                                            "The filter \"%{name}\" will be deleted permanently.")
                           :name name)
                   :yes-label (get-translation "generic.button.delete" "Delete")
                   :no-label (get-translation "generic.button.cancel" "Cancel")}
    :on-click #(re-frame/dispatch [::delete-view id])}])

(defn- post-columns []
  [{:header ""
    :field :actions
    :default-width 60
    :width 60
    :type :custom
    :text-align :center
    :resizable? false
    :sortable? false
    :systemcol? true
    :class ["action-menu"]
    :cell-fn (fn [_ row]
               [row-menu row (not (:can_edit row)) (row-menu-actions row)])}])

(defn- toolbar [_grid-props]
  (let [can-add? @(re-frame/subscribe [::can-add?])
        entity-types @(re-frame/subscribe [::entity-types])]
    [:div.grid-toolbar-right.custom-views-toolbar
     (into [dropdown/dropdown-button
            {:primary? true
             :disabled? (not can-add?)
             :tooltip (when-not can-add?
                        (get-translation "custom-views.new.limit-tooltip"
                                         "Free plans are limited to 5 filters"))
             :text (get-translation "custom-views.new" "New Filter")}
            {}]
           (for [entity-type entity-types]
             ^{:key entity-type}
             [dropdown/dropdown-menu-item
              {:text (entity-label entity-type)
               :on-click (fn [_]
                           (mixpanel/track "Filters Settings" {"Action" "New Filter"
                                                               "Entity" entity-type})
                           (re-frame/dispatch [::view-editor.events/open-create
                                               {:entity-type entity-type
                                                :initial-mode :ptql
                                                :on-saved [::view-saved]}]))}]))]))

(defn- blocked-delete-alert []
  (let [{:keys [message usages]} @(re-frame/subscribe [::blocked-delete])]
    [cf-form-common/convert-blocked-alert
     {:show? (some? message)
      :title (get-translation "custom-views.delete.blocked-title" "Cannot Delete Filter")
      :message message
      :usages usages
      :on-cancel #(re-frame/dispatch [::hide-blocked-delete])}]))

(defn- views-grid []
  (let [table-id (grid-table-id (:id @(re-frame/subscribe [::subs/current-project])))
        grid-state (reagent/atom {:module table-id})]
    (fn []
      (let [full-data? @(re-frame/subscribe [::full-data?])
            menu-collapsed? @(re-frame/subscribe [::navigation/project-level-menu-collapsed?])]
        [grid2/grid
         {:columns (columns full-data? @(re-frame/subscribe [::entity-types]))
          :post-columns (post-columns)
          :data @(re-frame/subscribe [::rows])
          :full-data? full-data?
          :loading? @(re-frame/subscribe [::loading?])
          :current-page @(re-frame/subscribe [::current-page])
          :total-count @(re-frame/subscribe [::total-count])
          :on-query-changed (rf-common/partial-dispatch [::refresh])
          :on-filter-changed (fn []
                               (swap! grid-state assoc :current-page 1)
                               (grid-storage/save-current-page table-id 1))
          :resizable? true
          :expanded? menu-collapsed?
          :on-expand-click (rf-common/partial-dispatch
                             [::navigation/set-project-level-menu-collapsed? (not menu-collapsed?)])
          :table-id table-id
          :grid-state grid-state
          :local-state-updating grid-state
          :grid-toolbar toolbar
          :columns-widths-save (reagent/partial
                                 grid-storage/save-column-sizes
                                 table-id)
          :columns-widths-load (reagent/partial
                                 grid-storage/load-column-sizes
                                 table-id)
          :sort-order-save (reagent/partial
                             grid-session-storage/save-sort-order
                             table-id)
          :sort-order-load (reagent/partial
                             grid-session-storage/load-sort-order
                             table-id)
          :per-page-save (reagent/partial
                           grid-storage/save-per-page
                           table-id)
          :per-page-load #(or
                            (grid-storage/load-per-page table-id)
                            default-per-page)
          :current-page-save (reagent/partial
                               grid-storage/save-current-page
                               table-id)
          :current-page-load (reagent/partial
                               grid-storage/load-current-page
                               table-id)}]))))

(defn index []
  [:div.custom-views-index-page
   [blocked-delete-alert]
   [:div.custom-views-header
    [project-settings-tabs/project-settings-tabs {}]
    [:h1.custom-views-page-title
     (get-translation "settings_sub_navbar.filters" "Filters")]]
   [:div.custom-views-content
    [views-grid]]])

(def main-controller
  {:start (fn [_] (re-frame/dispatch [::load!]))
   :stop (fn [_] (re-frame/dispatch [::reset]))})
